/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {MaterialReactTable, type MRT_ColumnDef, MRT_RowData, MRT_TableOptions, useMaterialReactTable} from 'material-react-table';
import type {Column} from '@tanstack/table-core';
import {tableHeaderGray, tableStripedRowDark, tableStripedRowLight} from '../../../constants/colors';
import {useEffect, useMemo, useState} from "react";
import {usePostRequest} from "../../../api/helper";
import {useAppSelector} from "../../../redux/hooks";
import {selectCurrentUser} from "../../../redux/ducks/AccessControl/slice";
import {argosFilteringOptions, postProcessColumns, TABLE_DEFAULT_PAGE_INDEX, TABLE_DEFAULT_PAGE_SIZE} from "./common";

interface IArgosTableProps<TData extends MRT_RowData> extends MRT_TableOptions<TData> {
  columns: MRT_ColumnDef<TData>[];
  data: TData[];
  uid?: string;
  tableRef?: React.MutableRefObject<any | null>;
}

export const ArgosTable = <TData extends MRT_RowData>(
  {
    columns,
    data,
    uid,
    tableRef,
    ...rest
  }: IArgosTableProps<TData>) => {
  const processedColumns = useMemo(() => {
    return postProcessColumns(columns)
  }, [columns])

  const [pinnedColumnsState, setPinnedColumnsState] = useState<any>({})

  const gmid = useAppSelector(state => selectCurrentUser(state).id)

  const postHandleUpdateUserTableState = usePostRequest('/HttpTriggerAcceptUser?type=updateTableState')
  useEffect(() => {
    if (Object.keys(pinnedColumnsState).length === 0) {
      return
    }
    postHandleUpdateUserTableState({
      gmid,
      uid,
      tableState: {
        pinnedColumns: pinnedColumnsState
      },
    }).then((response: any) => {
    }).catch((error: any) => {
      console.log(error)
    })
  }, [pinnedColumnsState, uid])

  const postHandleGetUserTableState = usePostRequest('/HttpTriggerAcceptUser?type=getTableState')
  useEffect(() => {
    postHandleGetUserTableState({
      gmid,
      uid,
    }).then((response: any) => {
      if (response?.pinnedColumns) {
        setPinnedColumnsState(response.pinnedColumns)
      }
    }).catch((error: any) => {
      console.log(error)
    })
  }, [gmid, uid]);

  const table = useMaterialReactTable({
    columns: processedColumns,
    data,

    // Global filter
    globalFilterFn: 'contains', // default global filter function
    enableGlobalFilter: true, // enable global filter by default
    getColumnCanGlobalFilter: (column: Column<any, unknown>) => {
      // Only allow global filtering on columns that are visible
      return column.getIsVisible()
    },

    columnFilterModeOptions: argosFilteringOptions,
    //Active Props 
    enableColumnResizing: true,             //resizing columns
    enableFullScreenToggle: true,           //full screen capability and button
    enableDensityToggle: false,             //change density of table with button
    enableColumnFilterModes: true,          //switch between multiple filter modes
    filterFromLeafRows: true,               //include parent rows when children are included in filter
    enableColumnFilters: true,              //enable all column filters for table
    enableStickyHeader: true,               //keeps header visible while scrolling
    enableStickyFooter: true,
    positionToolbarAlertBanner: "bottom",   //banners in toolbar show below button icon

    /*** Default props that do not need to be listed but are intentional **********************
     * columnResizeMode: 'onChange',           //default - change column size with the cursor
     * layoutMode:'grid-no-grow',              //default - columns do not move, fixes resizing issue
     * enableColumnActions: true,              //default - actions menu attached to each column
     * enableHiding: true,                     //default - allow columns to be hidden and vice versa
     * enableColumnPinning: false,             //default - allow columns to be pinned to left of table
     * enableFilters: true,                    //default - allows filter for table
     * enableSorting: true,                    //default - allows sorting
     *******************************************************************************************/

    enableColumnPinning: true,

    autoResetPageIndex: false,

    muiCircularProgressProps: {
      color: 'info',
      thickness: 5,
      size: 55,
    },
    muiTableBodyProps: {
      // Table row style - striping
      sx: {
        '& tr:nth-of-type(odd)': {
          backgroundColor: tableStripedRowDark,
        },
        '& tr:nth-of-type(even)': {
          backgroundColor: tableStripedRowLight,
        },
      },
    },
    muiTableHeadCellProps: {
      // Header cell text style
      sx: {
        // Left justify action button when visible
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'left',
        },
        color: 'black',         //header font color
        fontWeight: 'bold',     //header font weight
      },
    },
    muiTableHeadRowProps: {
      // Header row style
      sx: {
        backgroundColor: tableHeaderGray,
      },
    },

    muiTableContainerProps: ({table}) => {
      return ({
        sx: {
          maxHeight: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight ?? 0}px - ${table.refs.bottomToolbarRef.current?.offsetHeight ?? 0}px - ${table.refs.tableFooterRef.current?.offsetHeight ?? 0}px) !important`,
        },
      })
    },
    muiTablePaperProps: {
      sx: {height: '100% !important'},
    },

    positionActionsColumn: "last",

    ...rest, //accept props to override default table options

    initialState: {pagination: {pageSize: TABLE_DEFAULT_PAGE_SIZE, pageIndex: TABLE_DEFAULT_PAGE_INDEX, },...rest.initialState},

    onColumnPinningChange: (updater: any) => {
      setPinnedColumnsState((prev: any) => {
        const newState = updater(prev);
        setPinnedColumnsState(newState);
        return newState;
      })
    },

    state: {
      ...rest.state,
      columnPinning: pinnedColumnsState,
    },

    displayColumnDefOptions: {
      ...rest.displayColumnDefOptions,
      'mrt-row-actions': {
        ...rest.displayColumnDefOptions?.['mrt-row-actions'],
        enableColumnActions: true,
      },
    }
  });

  // Write the table instance into the parent ref (if provided).
  useEffect(() => {
    if (!tableRef) return;
    tableRef.current = table;
    return () => { tableRef.current = null; };
  }, [table, tableRef]);

  return <MaterialReactTable table={table}/>;
};

